import { isLethal, isRescue } from './card';
import {
  MAX_COPIES_PER_TITLE,
  OFFICIAL_SETUP,
  officialLethalFor,
  type ComboRules,
  type PlayerCount,
  type RoomConfig,
} from './config';
import { registerHouseRule } from './houseRules';
import { cardUid, type CardTitle, type CardUid, type PackId, type PlayerId } from './ids';
import { deepFreeze } from './immutable';
import type { CardRegistry, DeckCounts } from './pack';
import { nextBelow, type RngState } from './rng';
import { shuffle } from './shuffle';
import {
  emptyKnowledge,
  type CardInstance,
  type GameState,
  type Knowledge,
  type PlayerState,
} from './state';
import { t, type TextKey } from '../i18n';

/**
 * 开局：配置校验 + 发牌。
 *
 * 权威规则来源：`docs/rules.md` §3。那一节里所有"固定数字"到这里都变成配置项
 * （房主可改），但 §11.2 的校验必须过 —— 核心目标是
 * **配错了要在开局前给出人话提示，而不是让发牌中途炸掉**。
 */

// ============================================================
// 校验结果
// ============================================================

/** 一条配置问题。`field` 用来让 UI 定位到具体输入框 */
export interface ConfigProblem {
  /** 稳定的错误码，方便写测试和做 i18n */
  readonly code: string;
  /** 人话，直接可以显示给房主 */
  readonly message: string;
  /** 出问题的配置项路径，例如 `deck` 或 `lethalInDeck` */
  readonly field?: string;
}

/**
 * 校验通过后的配置：**所有 `null` 都已解析成具体数字**，
 * 并且附带派生出来的牌数统计。
 *
 * 这样发牌算法和 UI 都不用再自己算一遍。
 */
export interface ResolvedConfig {
  readonly playerCount: number;
  readonly deck: DeckCounts;
  readonly enabledPacks: readonly PackId[];
  readonly openingRescueCount: number;
  readonly openingHandSize: number;
  readonly lethalInDeck: number;
  readonly extraRescueInDeck: number;
  readonly combos: ComboRules;
  readonly deadPlayersCanNope: boolean;
  readonly firstPlayer: 'host' | 'random' | PlayerId;
  /** 下面这些是算出来的，给发牌和 UI 用 */
  readonly plan: DealPlan;
}

/** 发牌计划：先算清楚，再决定发不发得出来 */
export interface DealPlan {
  /** 牌堆总张数（`deck` 各项之和） */
  readonly totalCards: number;
  /** 救命牌总数 */
  readonly rescueTotal: number;
  /** 开局发给玩家的救命牌 */
  readonly rescueDealt: number;
  /** 其中放回牌堆的 */
  readonly rescueBackInDeck: number;
  /** 其中被移出游戏的 */
  readonly rescueRemoved: number;
  /** 致命牌总数 */
  readonly lethalTotal: number;
  /** 其中塞进牌堆的 */
  readonly lethalInDeck: number;
  /** 发完起手后，抽牌堆有几张 */
  readonly drawPileSize: number;
}

export type ValidationResult =
  | { readonly ok: true; readonly resolved: ResolvedConfig }
  | { readonly ok: false; readonly problems: readonly ConfigProblem[] };

/** 官方对「多余救命牌放回几张」的规定：2–3 人局封顶 2 张，4–5 人局全放回 */
export function officialExtraRescue(playerCount: number, spare: number): number {
  const cap = playerCount <= 3 ? 2 : Number.POSITIVE_INFINITY;
  return Math.min(spare, cap);
}

/**
 * 算出一份发牌计划。**纯计算，不做校验也不报错** ——
 * 校验逻辑拿它来判断"够不够发"，UI 也可以直接显示。
 */
export function planDeal(config: ResolvedConfig | RoomConfig, registry: CardRegistry): DealPlan {
  const deck = config.deck;
  const rescueTitles = new Set(registry.filter(isRescue).map((c) => c.id));
  const lethalTitles = new Set(registry.filter(isLethal).map((c) => c.id));

  const sumOf = (titles: ReadonlySet<CardTitle>): number =>
    Object.entries(deck).reduce((n, [title, count]) => n + (titles.has(title as CardTitle) ? count : 0), 0);

  const totalCards = Object.values(deck).reduce((a, b) => a + b, 0);
  const rescueTotal = sumOf(rescueTitles);
  const lethalTotal = sumOf(lethalTitles);

  const rescueDealt = config.playerCount * config.openingRescueCount;
  const spare = Math.max(0, rescueTotal - rescueDealt);

  const requested = config.extraRescueInDeck;
  const rescueBackInDeck =
    requested === null ? officialExtraRescue(config.playerCount, spare) : Math.min(requested, spare);
  const rescueRemoved = spare - rescueBackInDeck;

  const lethalInDeck = Math.min(config.lethalInDeck ?? config.playerCount - 1, lethalTotal);

  // 手里能发的牌 = 全部 − 已发给玩家的救命牌 − 移出游戏的救命牌 − 还没进牌堆的致命牌
  const poolForHands = totalCards - rescueDealt - rescueRemoved - lethalTotal;
  const handsNeeded = config.playerCount * config.openingHandSize;
  const drawPileSize = poolForHands - handsNeeded + lethalInDeck;

  return {
    totalCards,
    rescueTotal,
    rescueDealt,
    rescueBackInDeck,
    rescueRemoved,
    lethalTotal,
    lethalInDeck,
    drawPileSize,
  };
}

// ============================================================
// 校验
// ============================================================

function isNonNegInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0;
}

const SUPPORTED_PLAYER_COUNTS = [2, 3, 4, 5] as const;

/**
 * 校验房主配的游戏规则。
 *
 * ⚠️ **一次报出全部问题**，而不是碰到第一个就返回 ——
 * 房主改配置时应该一次看到所有要改的地方。
 *
 * 目标是把"配出一个发不出牌的局"挡在开局之前。
 */
export function validateConfig(config: RoomConfig, registry: CardRegistry): ValidationResult {
  const problems: ConfigProblem[] = [];
  const fail = (code: string, message: string, field?: string): void => {
    problems.push(field === undefined ? { code, message } : { code, message, field });
  };

  // ---- 玩家数 ----
  if (!SUPPORTED_PLAYER_COUNTS.includes(config.playerCount as (typeof SUPPORTED_PLAYER_COUNTS)[number])) {
    fail('playerCount/range', t('core.setup.playerCountRange', { count: config.playerCount }), 'playerCount');
  }

  // ---- 数据包 ----
  const requiredPacks = registry.packs.filter((p) => p.required === true).map((p) => p.id);
  for (const pack of requiredPacks) {
    if (!config.enabledPacks.includes(pack)) {
      fail('packs/missingRequired', t('core.setup.missingRequiredPack', { pack }), 'enabledPacks');
    }
  }
  const registryPacks = registry.packs.map((p) => p.id).join(',');
  if (config.enabledPacks.join(',') !== registryPacks) {
    fail(
      'packs/mismatch',
      t('core.setup.packsMismatch', {
        configured: config.enabledPacks.join(', '),
        registry: registryPacks,
      }),
      'enabledPacks',
    );
  }

  // ---- 牌堆构成 ----
  for (const [title, count] of Object.entries(config.deck)) {
    if (registry.get(title as CardTitle) === undefined) {
      fail('deck/unknownCard', t('core.setup.unknownCard', { title }), `deck.${title}`);
      continue;
    }
    if (!isNonNegInt(count)) {
      fail('deck/badCount', t('core.setup.badCount', { title, count: String(count) }), `deck.${title}`);
      continue;
    }
    if (count > MAX_COPIES_PER_TITLE) {
      fail(
        'deck/tooMany',
        t('core.setup.tooMany', { title, max: MAX_COPIES_PER_TITLE, count }),
        `deck.${title}`,
      );
    }
  }

  // ---- 起手 ----
  if (!isNonNegInt(config.openingRescueCount)) {
    fail('opening/badRescue', t('core.setup.badRescue'), 'openingRescueCount');
  }
  if (!isNonNegInt(config.openingHandSize)) {
    fail('opening/badHandSize', t('core.setup.badHandSize'), 'openingHandSize');
  }

  // ---- 能力牌是否齐备 ----
  const rescueCards = registry.filter(isRescue);
  const lethalCards = registry.filter(isLethal);
  if (rescueCards.length === 0) {
    fail('content/noRescue', t('core.setup.noRescue'));
  }
  if (lethalCards.length === 0) {
    fail('content/noLethal', t('core.setup.noLethal'));
  }

  // ---- 致命牌数量 ----
  const lethalTotal = lethalCards.reduce((n, c) => n + (config.deck[c.id] ?? 0), 0);
  const lethalInDeck = config.lethalInDeck ?? config.playerCount - 1;
  if (!isNonNegInt(lethalInDeck)) {
    fail('lethal/badValue', t('core.setup.badLethal'), 'lethalInDeck');
  } else {
    if (lethalInDeck > lethalTotal) {
      fail(
        'lethal/notEnoughCards',
        t('core.setup.notEnoughLethal', { needed: lethalInDeck, have: lethalTotal }),
        'lethalInDeck',
      );
    }
    // 少于「人数 − 1」时，最多只能死掉 lethalInDeck 个人，剩下的没法分出胜负
    if (lethalInDeck < config.playerCount - 1) {
      fail(
        'lethal/tooFewToFinish',
        t('core.setup.lethalTooFew', { min: config.playerCount - 1, count: lethalInDeck }),
        'lethalInDeck',
      );
    }
  }

  // ---- 多余救命牌 ----
  if (config.extraRescueInDeck !== null && !isNonNegInt(config.extraRescueInDeck)) {
    fail('rescue/badValue', t('core.setup.badExtraRescue'), 'extraRescueInDeck');
  }

  // 上面这些如果有问题，先别算计划 —— 数字可能是坏的
  if (problems.length > 0) return { ok: false, problems };

  // ⚠️ 必须拿**原始配置**算计划 —— 只有 `null` 才会触发官方规则
  //（拿已经解析过的值去算会把占位数字当成真实配置）
  const plan = planDeal(config, registry);

  const resolved: ResolvedConfig = {
    playerCount: config.playerCount,
    deck: config.deck,
    enabledPacks: config.enabledPacks,
    openingRescueCount: config.openingRescueCount,
    openingHandSize: config.openingHandSize,
    lethalInDeck,
    extraRescueInDeck: plan.rescueBackInDeck, // 已解析：null 在这里变成具体张数
    combos: {
      ...config.combos,
      // 五张不同名没开时，它的子选项无意义 —— 归一化掉，不算错误
      fiveDifferentCanTakeDefuse: config.combos.fiveDifferent
        ? config.combos.fiveDifferentCanTakeDefuse
        : false,
    },
    deadPlayersCanNope: config.deadPlayersCanNope,
    firstPlayer: config.firstPlayer,
    plan,
  };

  // ---- 发得出来吗 ----
  if (plan.rescueDealt > plan.rescueTotal) {
    fail(
      'deal/notEnoughRescue',
      t('core.setup.notEnoughRescue', {
        perPlayer: config.openingRescueCount,
        needed: plan.rescueDealt,
        have: plan.rescueTotal,
      }),
      'deck',
    );
  }

  const handsNeeded = config.playerCount * config.openingHandSize;
  const poolForHands = plan.totalCards - plan.rescueDealt - plan.rescueRemoved - plan.lethalTotal;
  if (handsNeeded > poolForHands) {
    fail(
      'deal/notEnoughCards',
      t('core.setup.notEnoughCards', {
        needed: handsNeeded,
        players: config.playerCount,
        handSize: config.openingHandSize,
        remaining: Math.max(0, poolForHands),
        suggested: handsNeeded + plan.rescueDealt + plan.rescueRemoved + plan.lethalTotal,
      }),
      'deck',
    );
  }

  if (plan.drawPileSize <= 0) {
    fail('deal/emptyDrawPile', t('core.setup.emptyDrawPile'), 'deck');
  }

  if (problems.length > 0) return { ok: false, problems };

  return { ok: true, resolved };
}

/**
 * **这一局实际"在场上"的每种牌有几张。**
 *
 * `config.deck` 描述的是"牌堆构成"（要放几张），但开局会把多余的救命牌 /
 * 致命牌**移出游戏**（`setup()` 里那些牌根本不建实例，见那段注释）。
 * 记牌器 / Bot 需要的是"实际存在于场上的张数" —— 直接用 `deck` 会多算，
 * 于是"剩余未知"永远对不上。
 *
 * ⚠️ 纯函数；结果只取决于 `config` + 注册表。
 */
export function inPlayDeck(config: RoomConfig, registry: CardRegistry): DeckCounts {
  const validated = validateConfig(config, registry);
  const deck = validated.ok ? validated.resolved.deck : registry.defaultDeck();
  const plan = validated.ok ? validated.resolved.plan : null;

  const out: Record<string, number> = {};
  for (const title of Object.keys(deck)) out[title] = deck[title] ?? 0;
  if (plan === null) return out;

  /**
   * 按一批牌名依次消费预算：`budget` 是这一类"实际建了实例"的总数，
   * 和 `setup()` 里 `Math.min(deck[title], 剩余预算)` 是同一套顺序，
   * 所以两边算出来的每张牌张数一致。
   */
  const keep = (titles: readonly CardTitle[], budget: number): void => {
    let left = Math.max(0, budget);
    for (const title of titles) {
      const have = out[title] ?? 0;
      const used = Math.min(have, left);
      out[title] = used;
      left -= used;
    }
  };
  keep(registry.filter(isRescue).map((card) => card.id), plan.rescueDealt + plan.rescueBackInDeck);
  keep(registry.filter(isLethal).map((card) => card.id), plan.lethalInDeck);
  return out;
}

// ============================================================
// 大厅
// ============================================================

/**
 * 造一个还在大厅阶段的房间状态。
 *
 * 房主先建房间，其他人用 `lobby/join` 进来，房主用 `lobby/setConfig` 调游戏规则，
 * 最后 `game/start` 才真正发牌。
 */
export function createLobbyState(params: {
  readonly host: PlayerId;
  readonly config: RoomConfig;
}): GameState {
  return deepFreeze({
    phase: 'lobby',
    host: params.host,
    config: params.config,
    players: [],
    cards: [],
    drawPile: [],
    discardPile: [],
    turn: null,
    resolution: null,
    decision: null,
    knowledge: {},
    rng: { seed: 0 },
    winner: null,
  });
}

// ============================================================
// 发牌
// ============================================================

export interface PlayerSeed {
  readonly id: PlayerId;
  readonly name: string;
}

export interface SetupParams {
  /**
   * 存进 `GameState` 的**原始**配置（房主填的那份，`null` 保持 `null`）。
   * 不存解析结果 —— 它只描述"开局那一刻"，之后就没意义了。
   */
  readonly config: RoomConfig;

  /** 发牌计划等解析结果，只在发牌时用 */
  readonly resolved: ResolvedConfig;

  readonly registry: CardRegistry;
  readonly host: PlayerId;
  readonly players: readonly PlayerSeed[];
  /** 初始随机状态。种子由 `game/start` 动作带进来 —— 它也是棋谱的一部分 */
  readonly rng: RngState;
}

/**
 * 按官方顺序发牌（`docs/rules.md` §3）：
 *
 * 1. 取出全部致命牌放一边
 * 2. 取出救命牌，每人发 N 张
 * 3. 多余的救命牌：一部分放回牌堆，其余移出游戏
 * 4. 洗牌，每人发 K 张（**此时牌堆里还没有致命牌**，所以起手不可能摸到炸弹）
 * 5. 把 M 张致命牌塞进牌堆，其余移出游戏
 * 6. 再洗一次，成为抽牌堆
 *
 * ⚠️ 第 3 步和第 4 步的顺序不能反：多余的救命牌是先回牌堆再发的，
 * 所以玩家**有可能**在起手 7 张里摸到额外的救命牌。这是官方的本意。
 */
export function createInitialState(params: SetupParams): GameState {
  const { config, resolved, registry, players } = params;
  const plan = resolved.plan;

  // ---- ① 建卡表。uid 就是数组下标，所以顺序必须确定 ----
  const cards: CardInstance[] = [];
  const create = (title: CardTitle, count: number): CardUid[] => {
    const uids: CardUid[] = [];
    for (let i = 0; i < count; i++) {
      uids.push(cardUid(cards.length));
      cards.push({ title });
    }
    return uids;
  };

  const rescueTitles = registry.filter(isRescue).map((c) => c.id);
  const lethalTitles = registry.filter(isLethal).map((c) => c.id);
  const isAbility = (t: CardTitle): boolean => rescueTitles.includes(t) || lethalTitles.includes(t);

  // 救命牌只建「发出去的 + 放回牌堆的」。被移出游戏的**根本不建实例** ——
  // 这样就有个干净的不变量：state.cards 里的每张牌都在手牌 / 抽牌堆 / 弃牌堆之一。
  const rescueUids: CardUid[] = [];
  for (const title of rescueTitles) {
    const want = Math.min(resolved.deck[title] ?? 0, plan.rescueDealt + plan.rescueBackInDeck - rescueUids.length);
    if (want > 0) rescueUids.push(...create(title, want));
  }

  // 致命牌同理：只有塞进牌堆的那几张才建实例
  const lethalUids: CardUid[] = [];
  for (const title of lethalTitles) {
    const want = Math.min(resolved.deck[title] ?? 0, plan.lethalInDeck - lethalUids.length);
    if (want > 0) lethalUids.push(...create(title, want));
  }

  // 其余所有牌全量建（它们都会进牌堆）
  const otherUids: CardUid[] = [];
  for (const def of registry.cards) {
    if (isAbility(def.id)) continue;
    otherUids.push(...create(def.id, resolved.deck[def.id] ?? 0));
  }

  // ---- ② 每人先发救命牌 ----
  const hands: CardUid[][] = players.map(() => []);
  const rescueRest = [...rescueUids];
  for (const hand of hands) {
    for (let i = 0; i < resolved.openingRescueCount; i++) {
      const uid = rescueRest.shift();
      if (uid === undefined) throw new Error(t('core.setup.rescueShortfall'));
      hand.push(uid);
    }
  }

  // ---- ③④ 剩下的救命牌回牌堆，洗牌，发随机牌 ----
  const pool = [...otherUids, ...rescueRest];
  const firstShuffle = shuffle(pool, params.rng);
  let rngState = firstShuffle.rng;
  const pile: CardUid[] = [...firstShuffle.items];

  for (const hand of hands) {
    for (let i = 0; i < resolved.openingHandSize; i++) {
      const uid = pile.pop();
      if (uid === undefined) throw new Error(t('core.setup.handShortfall'));
      hand.push(uid);
    }
  }

  // ---- ⑤⑥ 塞入致命牌，再洗一次，成为抽牌堆 ----
  const secondShuffle = shuffle([...pile, ...lethalUids], rngState);
  rngState = secondShuffle.rng;
  const drawPile = secondShuffle.items;

  // ---- 起始玩家 ----
  let firstSeat = 0;
  if (resolved.firstPlayer === 'random' && players.length > 0) {
    const draw = nextBelow(rngState, players.length);
    rngState = draw.state;
    firstSeat = draw.value;
  } else if (resolved.firstPlayer !== 'host') {
    // 指定了某个玩家：找不到就退回座位 0（第一个）
    const at = players.findIndex((p) => p.id === resolved.firstPlayer);
    if (at >= 0) firstSeat = at;
  }

  const playerStates: PlayerState[] = players.map((seed, index) => ({
    id: seed.id,
    name: seed.name,
    hand: hands[index] ?? [],
    alive: true,
    connected: true,
  }));

  const knowledge: Record<string, Knowledge> = {};
  for (const player of playerStates) knowledge[player.id] = emptyKnowledge();

  const firstPlayer = playerStates[firstSeat];

  return deepFreeze({
    phase: 'playing',
    host: params.host,
    config,
    players: playerStates,
    cards,
    drawPile,
    discardPile: [],
    turn:
      firstPlayer === undefined
        ? null
        : {
            player: firstPlayer.id,
            turnsRemaining: 1,
            fromAttack: false,
            attackSource: null,
          },
    resolution: null,
    decision: null,
    knowledge,
    rng: rngState,
    winner: null,
  });
}


// ============================================================
// 游戏规则登记（开局 / 致命牌）
// ============================================================
//
// ⚠️ 登记放在**使用这些字段的功能实现**里（发牌就是这里），不集中到一个
// ui/rules/core.ts —— 否则机制改了、字段废了，那份集中的表没人会想起来改
// （nopeWindowMs 就是这么变成死设置的）。

// ⚠️ 存键不存文案：登记发生在导入期，t() 会把语言冻住。渲染时再 t()（见 room.ts）
const OPENING_SECTION: TextKey = 'core.section.opening';
const LETHAL_SECTION: TextKey = 'core.section.lethal';

/** 官方"多余的救命牌放回几张"（2–3 人 2 张、4 人 2 张、5 人 1 张） */
function officialExtraRescueInDeck(playerCount: number): number {
  return OFFICIAL_SETUP[playerCount as PlayerCount]?.extraRescueInDeck ?? 2;
}

registerHouseRule({
  id: 'openingHandSize',
  section: OPENING_SECTION,
  label: 'core.rule.openingHandSize',
  control: { kind: 'number', min: 0, max: 12 },
  value: (c) => c.openingHandSize,
  patch: (v) => ({ openingHandSize: Number(v) }),
});

registerHouseRule({
  id: 'openingRescueCount',
  section: OPENING_SECTION,
  label: 'core.rule.openingRescueCount',
  control: { kind: 'number', min: 0, max: 4 },
  value: (c) => c.openingRescueCount,
  patch: (v) => ({ openingRescueCount: Number(v) }),
});

registerHouseRule({
  id: 'lethalInDeck',
  section: LETHAL_SECTION,
  label: 'core.rule.lethalInDeck',
  control: {
    kind: 'auto-number',
    min: 0,
    max: 10,
    autoValue: (c) => officialLethalFor(c.playerCount),
    autoNote: (c) =>
      t('core.rule.lethalInDeck.auto', { players: c.playerCount, count: officialLethalFor(c.playerCount) }),
    manualFallback: (c) => officialLethalFor(c.playerCount),
  },
  value: (c) => c.lethalInDeck,
  patch: (v) => ({ lethalInDeck: v === null ? null : Number(v) }),
});

registerHouseRule({
  id: 'extraRescueInDeck',
  section: LETHAL_SECTION,
  label: 'core.rule.extraRescueInDeck',
  control: {
    kind: 'auto-number',
    min: 0,
    max: 8,
    autoValue: (c) => officialExtraRescueInDeck(c.playerCount),
    autoNote: (c) =>
      t('core.rule.extraRescueInDeck.auto', {
        players: c.playerCount,
        count: officialExtraRescueInDeck(c.playerCount),
      }),
    manualFallback: () => 2,
  },
  value: (c) => c.extraRescueInDeck,
  patch: (v) => ({ extraRescueInDeck: v === null ? null : Number(v) }),
});
